<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import NgChat from "@/components/Chat/NgChat.vue";
import NgVideo from "@/components/Video/NgVideo.vue";
import BottomBar from "@/components/Layout/BottomBar.vue";
import { useIncidentHub } from '@/composables/useIncidentHub';
import { useVisualViewport } from '@/composables/useVisualViewport';
import { ViewMode } from "@/enums/ViewMode";
import { useChat } from '@/composables/useChat';
import { useVideo } from '@/composables/useVideo';

useVisualViewport();

const incidentHub = useIncidentHub();

const currentView = ref<ViewMode>(ViewMode.Chat);
const loading = ref<boolean>(true);

const { loadVideo, wasActivated: videoIsAllowed, stopVideo } = useVideo();
const {  loadChat } = useChat();

onMounted(async () => {
  const signalRConnection = await incidentHub.startSignalRConnection();

  const incident = await incidentHub.getWebAppIncident();

  if (!incident) {
    throw new Error("Incident not found");
  }

  redirectToClosedPageWhenIncidentClosed(incident.isClosed);

  signalRConnection.on("IncidentClosed", () => redirectToClosedPageWhenIncidentClosed(true));

  signalRConnection.on("VideoCancelled", async () => { currentView.value = ViewMode.Chat });

  await loadChat(incident);
  await loadVideo(incident);

  const chatMessages = incident.chatMessages;
  const chatIsEmpty = chatMessages == null || chatMessages.length === 0;
  if (chatIsEmpty && videoIsAllowed.value) {
    currentView.value = ViewMode.Video;
  }

  loading.value = false;
});

onUnmounted(async () => {
  try {
    await stopVideo();

  } catch (e) {
    console.warn("Video cleanup failed", e);
  }
});

function redirectToClosedPageWhenIncidentClosed(isClosed: boolean) {
  if (isClosed) {
    window.location.href = "https://ng-sos.com/";
  }
}
</script>

<template>
  <div class="loading" v-show="loading">
    <img src="@/assets/logo.svg" alt="NG-SOS" class="loading-logo animate-pulse" />
  </div>
  <div
    class="video-chat-layout"
    :class="{ 'hide-bottom-bar': !videoIsAllowed }"
    v-show="!loading"
  >
    <div class="content-area">
      <NgVideo
        v-if="videoIsAllowed"
        v-show="currentView === ViewMode.Video"
      />

      <NgChat
        v-show="currentView === ViewMode.Chat"
        @message-received="() => currentView = ViewMode.Chat"
        @picture-in-picture-video-clicked="() => currentView = ViewMode.Video"
      />
    </div>

    <BottomBar
      v-show="videoIsAllowed"
      class="bottom-bar"
      :current-view="currentView"
      @viewChanged="mode => currentView = mode"
    />
  </div>
</template>

<style scoped>
.video-chat-layout {
  position: fixed;
  top: 0;
  left: 0;
  display: grid;
  grid-template-rows: 1fr var(--bottom-bar-height);
  grid-template-areas:
    "content"
    "bottom";
  height: var(--visual-viewport-height, 100vh);
  width: 100vw;
  overflow: hidden;
  transform: translateY(var(--visual-viewport-offset-top, 0px));

  --bottom-bar-height: 70px;

  &.hide-bottom-bar {
    --bottom-bar-height: 0px;
  }
}

.content-area {
  grid-area: content;
  position: relative;
  overflow: hidden;
  min-height: 0;
}

.bottom-bar {
  grid-area: bottom;
}

.loading {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  background: var(--color-chat-background, #000);
  z-index: 1000;
}

.loading-logo {
  width: 60vw;
  min-width: 200px;
  height: auto;
  max-width: 100%;
}
</style>
